import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useMediaQuery, useTheme } from "@mui/material";
import { CircularProgress, Typography, Button, Collapse } from "@mui/material";
import { useTranslation } from "react-i18next";
import BasicTable from "../../../Components/Table/Material/index.js";
import { useModal } from "../../../Hooks/useModal.js";
import Modal from "../../../Components/Modal/index.js";
import UserActionModal from "../../../Components/Modal/UserActionModal.js";
import ErrorHandler from "../../../Components/Shared/ErrorHandler.js";
import useApi from "../../../Hooks/useApi.js";
import { useNavigate } from "react-router-dom";
import { usePagination } from "../../../Hooks/usePagination.js";
import TextField from "../../../Components/UI/Inputs/TextField.js";
import DropDown from "../../../Components/UI/Inputs/DropDown.js";
import debounce from "lodash/debounce";
import showToast from "../../../Helpers/toast.js";
import ConfirmModal from "../../../Components/Modal/ConfirmModal.js";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSearch, faXmark } from "@fortawesome/free-solid-svg-icons";
import NavigateButton from "../../../Components/UI/Buttons/NavigateButton.js";

const deactivateUser = async ({ userId, email }, request, refetch, onClose) => {
  try {
    const response = await request("POST", "/v1/user/deactivate", {
      data: { id: userId, email: email },
    });
    showToast({ message: response.message });
    setTimeout(() => {
      onClose();
      refetch();
    }, 1500);
  } catch (error) {}
};

export const SummaryCard = ({ title, value }) => {
  return (
<div
  className="p-4 shadow-sm bg-gray-100 
             hover:bg-gradient-to-r hover:from-[#e0f7ff] hover:to-[#f0fbff] 
             text-center flex  justify-center items-center gap-2 
             border border-[#7ddbff] hover:border-[#41b2df] 
             w-auto sm:w-52 lg:w-60 
           rounded-md  cursor-pointer 
             transform transition-all duration-300  hover:shadow-md"
>
  <h3 className="text-gray-600 text-sm">{title}</h3>
  <p className="text-xl font-bold text-gray-800">{value}</p>
</div>

  );
};

export default function UserManagement() {
  const [users, setUsers] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [designations, setDesignations] = useState([]);
  const [modalData, setModalData] = useState(null);
  const { isOpen, openModal, closeModal } = useModal();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { t } = useTranslation();
  const { request, error, loading } = useApi();
  const [searchTerm, setSearchTerm] = useState("");
  const [designationName, setDesignationName] = useState("");
  const [departmentName, setDepartmentName] = useState("");
  const [userStatus, setUserStatus] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [isConfirmOpen, setConfirmOpen] = useState(false);
  const [generalInfo, setGeneralInfo]= useState(null);

  const navigate = useNavigate();
  const handleDeactivateClick = () => {
    setConfirmOpen(true); // Open the confirmation modal
  };

  const handleConfirmDeactivate = () => {
    const refetchAll = () => {
      fetchUsers(searchTerm, designationName, departmentName, userStatus);
      fetchGeneralInfo();
    };
    deactivateUser(
      modalData,
      request,
      refetchAll,
      closeModal
    );
    setConfirmOpen(false); // Close the modal
  };

  const handleCloseModal = () => {
    setConfirmOpen(false); // Close the modal
  };
  const paginateProps = usePagination();

  const columns = [
    {
      id: "fullName",
      label: t("user_management.name"),
      minWidth: 170,
      renderCell: (row) => (
        <div className="flex items-center">
          <span
            style={{
              backgroundColor: row?.status === "active" ? "green" : "gray",
            }}
            className={`w-3 h-3 rounded-full mr-2`}
          />
          {row?.fullName}
        </div>
      ),
    },
    { id: "userType", label: t("user_management.role"), minWidth: 100 },
    {
      id: "department",
      label: `${t("user_management.department")}`,
      minWidth: 200,
      align: "center",
      renderCell: (row) => <div className="">{row?.department}</div>,
    },
    {
      id: "designation",
      label: `${t("user_management.designation")}`,
      minWidth: 200,
      align: "left",
      renderCell: (row) => (
        <div className="">
          <p className="text-sm">{row?.designation}</p>
        </div>
      ),
    },
    {
      id: "action",
      label: t("user_management.action"),
      minWidth: 170,
      align: "center",
      renderCell: (row) => {
        return row?.status === "inactive" ? (
          <button
            onClick={() => handleActionClick(row)}
            className={`w-[130px] bg-[#37a45be6] border border-gray-300  text-white rounded-md  p-1.5`}
          >
            Activate
          </button>
        ) : (
          <div className="lg:flex flex-col gap-2 items-center">
            <button
              onClick={() => handleActionClick(row)}
              className={`w-[130px] bg-[#1976d2] border border-gray-300  text-white rounded-md  p-1.5`}
            >
              Manage User
            </button>{" "}
            <button
              onClick={() => {
                //handleActionClick(row);
                setModalData(row);
                handleDeactivateClick();
              }}
              className={`w-[130px] bg-red-500 border border-gray-300  text-white rounded-md  p-1.5`}
            >
              Deactivate
            </button>
          </div>
        );
      },
    },
  ];

    const summaryData = useMemo(() => {
    if (!generalInfo) return [];
    return [
      { id: 1, title: "Total Users", value: paginateProps.totalData },
      { id: 2, title: "Departments", value: Math.max(departments.length - 1, 0) },
      { id: 3, title: "Active Users", value: generalInfo.activeUserNumber ?? 0},
      { id: 4, title: "Inactive Users", value: generalInfo.inactiveUserNumber ?? 0 },
    ];
  }, [paginateProps.totalData, departments.length, generalInfo]);


  const debouncedFetchUsers = useCallback(
    debounce((searchTerm, designationName, departmentName, userStatus) => {
      fetchUsers(searchTerm, designationName, departmentName, userStatus); fetchGeneralInfo();
    }, 300),
    []
  );

  useEffect(() => {
    fetchDepartments();
    fetchDesignations();
  }, []);

  useEffect(() => {
    debouncedFetchUsers(
      searchTerm,
      designationName,
      departmentName,
      userStatus
    );
  }, [
    searchTerm,
    designationName,
    departmentName,
    userStatus,
    debouncedFetchUsers,
  ]);

  useEffect(() => {
    fetchUsers(searchTerm, designationName, departmentName, userStatus);
    fetchGeneralInfo();
  }, [paginateProps.page, paginateProps.rowsPerPage, departmentName]); // Added departmentName to dependencies

  const fetchDepartments = async () => {
    try {
      const response = await request("GET", "/v1/departments");
      
      const formattedDepartments = [
        { name: "All", value: "" },
        ...response.map((dept) => ({
          name: dept.name,
          value: dept.name,
        })),
      ];
      setDepartments(formattedDepartments);
    } catch (err) {
      console.error("Error fetching departments:", err);
    }
  };

  const fetchDesignations = async () => {
    try {
      const response = await request("GET", "/v1/designations");
      const formattedDesignations = [
        { name: "All", value: "" },
        ...response.map((desig) => ({
          name: desig.name,
          value: desig.name,
        })),
      ];
      setDesignations(formattedDesignations);
    } catch (err) {
      console.error("Error fetching designations:", err);
    }
  };


  const fetchUsers = async (
    searchTerm,
    designationName,
    departmentName,
    userStatus
  ) => {
    try {
      const filterData = {
        // isDeleted: false,
      };

      if (searchTerm) filterData.email = searchTerm?.trim();
      if (designationName) filterData.designationName = designationName;
      if (departmentName) filterData.departmentName = departmentName;
      if (userStatus) filterData.userStatus = userStatus;

      const response = await request(
        "POST",
        `/v1/user/list?offset=${paginateProps.page}&limit=${paginateProps.rowsPerPage}`,
        {
          data: filterData,
        }
      );

      setUsers(response.content);
      paginateProps.setTotalData(response.totalElements);
    } catch (err) {
      console.error("Error fetching users:", err);
    }
  };


const fetchGeneralInfo = useCallback(async () => {
      try {
        const response = await request("GET", "/v1/user/general-info");
    
      
        setGeneralInfo(response);
      } catch (error) {
        console.log(error);
      }
    }, [request]); // Add 'request' as a dependency


  const handleActionClick = (row) => {
    setModalData(row);
    openModal();
  };

  const handleAddUser = (type) => {
    navigate(type == "imis" ? "/add-imis-user" : "/add-user");
  };

  const handleResetFilters = () => {
    setSearchTerm("");
    setDesignationName("");
    setDepartmentName("");
    setUserStatus("");
    fetchUsers("", "", "", "");
  };

  const toggleFilters = () => {
    setShowFilters(!showFilters);
  };

 if (!generalInfo) return <p>Loading...</p>;

  return (
    <div className="bg-white relative h-full">

          <div className="flex flex-col h-full text-start">
      <div className="flex justify-between">
        <h2 className="" style={{fontSize:"1.875rem"}}>Users</h2>
         <NavigateButton text='back' />
       </div>


   <div className="flex flex-wrap items-center gap-5 pb-6">
      {summaryData.map((item) => (
        <SummaryCard key={item.id} title={item.title} value={item.value} />
      ))}
    </div>
      <div className={`flex-grow border-4 !border-[#E0F5FD] overflow-auto h-full `}>
        {/* Content area */}
 <div className="sticky top-0 p-4 z-10 bg-white bg-opacity-95">
          <div className=" flex flex-wrap items-center justify-between gap-2  ">
            <div className="flex items-center gap-2">
              <Button variant="contained" onClick={handleAddUser}>
                {t("user_management.add_user")}
              </Button>
              <Button variant="contained" onClick={() => handleAddUser("imis")}>
                {"Add IMIS User"}
              </Button>
              <Button
                variant="outlined"
                startIcon={<FontAwesomeIcon icon={"fa-filter"} />}
                onClick={toggleFilters}
              >
                {showFilters ? "Hide Filters" : "Show Filters"}
              </Button>
            </div>
            {(searchTerm ||
              designationName ||
              departmentName ||
              userStatus) && (
              <Button
                variant="outlined"
                color="secondary"
                startIcon={<FontAwesomeIcon icon={faXmark}/>}
                onClick={handleResetFilters}
              >
                Reset Filters
              </Button>
            )}
          </div>

          <Collapse in={showFilters} className="">
            <div
              className={`pt-2 grid ${
                isMobile ? "grid-cols-1" : "grid-cols-2 md:grid-cols-4"
              } gap-4 border-b-2 pb-2 border-dashed`}
            >
              <TextField
                value={searchTerm}
                name={"searchTerm"}
                onValueChange={(_, value) => {
                  setSearchTerm(value);
                }}
                icon={<FontAwesomeIcon icon={faSearch} />}
                placeholder="Search by email"
                fullWidth
              />
              <DropDown
                setter={(value) => setDesignationName(value)}
                placeholder="Select Designation"
                value={designationName}
                options={designations}
                fullWidth
              />
              <DropDown
                setter={(value) => setDepartmentName(value)}
                placeholder="Select Department"
                value={departmentName}
                options={departments}
                fullWidth
              />
              <DropDown
                setter={(value) => setUserStatus(value)}
                placeholder="Select User Status"
                value={userStatus}
                options={[
                  { name: "All", value: "" },
                  { name: "Active", value: "active" },
                  { name: "Inactive", value: "inactive" },
                ]}
                fullWidth
              />
            </div>
          </Collapse>
        </div>

        {loading ? (
          <CircularProgress />
        ) : error ? (
          <ErrorHandler errors={error} />
        ) : users?.length > 0 ? (
          <BasicTable
            isLoading={loading}
            columns={columns}
            data={users}
            maxHeight="15000"
            paginateProps={{
              ...paginateProps,
              onPageChange: (_, newPage) => paginateProps.setPage(newPage),
              onRowsPerPageChange: (event) => {
                paginateProps.setRowsPerPage(
                  Number.parseInt(event.target.value, 10)
                );
                paginateProps.setPage(0);
              },
            }}
          />
        ) : (
          <Typography variant="body1" className="text-center py-4">
            No users found.
          </Typography>
        )}

        <Modal isOpen={isOpen} onClose={closeModal}>
          {modalData && (
            <UserActionModal
              onClose={closeModal}
              data={modalData}
              refetch={() =>
              {
                     fetchUsers(
                  searchTerm,
                  designationName,
                  departmentName,
                  userStatus
                );
                fetchGeneralInfo()

              }
           
              }
            />
          )}
        </Modal>

        <ConfirmModal
          isOpen={isConfirmOpen}
          onClose={handleCloseModal}
          onConfirm={handleConfirmDeactivate} // Call when "Yes" is
          label={"Are you sure you want to deactivate this user?"}
        />
      </div>



    </div>


      {/* <Common headline={"Users"} padding={false}> */}


       
      {/* </Common> */}
    </div>
  );
}
